/**
 * Pages for an audience, linked from the landing: /schools (Make It for
 * schools) and /special-needs (Make It for special educational needs). Built
 * with the landing's pieces and style; texts in audienceText.ts.
 */
import { useEffect, useRef, type ReactNode } from "react";
import { libraryUrl } from "../data/media";
import { currentLanguage } from "../player/i18n";
import { RTL_LANGUAGES } from "../player/rtl";
import { audiencePath, PLAY_ORIGIN, type Audience } from "../site";
import { Icon } from "./widgets";
import { Browser, Confetti, Feature, Heading, LanguagePicker, LINKS, Tablet, useReveal } from "./Landing";
import { landingText } from "./landingText";
import { audienceText } from "./audienceText";
import "./landing.css";

const TILE_ICONS: Record<Audience, string[]> = {
  schools: ["games", "class", "ranking", "devices", "lock", "subscription"],
  special: ["speaker", "reinforcement", "mic", "font", "navigation", "drawing"],
};

/** Screenshots of the web player and editor next to each section (public/landing). */
const ART: Record<Audience, { hero: ReactNode; sections: ReactNode[] }> = {
  schools: {
    hero: (
      <>
        <Browser className="lp-hero-editor" src="/landing/editor.webp" />
        <Tablet className="lp-hero-tablet" src="/landing/quiz.webp" />
      </>
    ),
    sections: [<Tablet key="a" src="/landing/game-colours.webp" />, <Tablet key="b" src="/landing/quiz.webp" />, <Browser key="c" src="/landing/editor-emotions.webp" />],
  },
  special: {
    hero: (
      <>
        <Browser className="lp-hero-editor" src="/landing/editor-emotions.webp" />
        <Tablet className="lp-hero-tablet" src="/landing/game-emotions.webp" />
      </>
    ),
    sections: [<Browser key="a" src="/landing/editor-emotions.webp" />, <Tablet key="b" src="/landing/game-shapes.webp" />, <Tablet key="c" src="/landing/game-letters.webp" />],
  },
};

const TINTS = ["purple", "orange", "red"];

export function AudiencePage({ audience }: { audience: Audience }) {
  const A = audienceText();
  const L = landingText();
  const T = A.pages[audience];
  const lang = currentLanguage();
  const page = useRef<HTMLDivElement>(null);
  useReveal(page);
  useEffect(() => {
    document.title = `${T.title} · Make It`;
  }, [T.title]);
  const other: Audience = audience === "schools" ? "special" : "schools";
  const start = "/?signup";
  const second =
    audience === "schools" ? (
      <a className="lp-btn lp-btn-outline lp-btn-big" href={`mailto:${LINKS.email}?subject=${encodeURIComponent("Make It · Schools")}`}>
        {T.second}
      </a>
    ) : (
      <a className="lp-btn lp-btn-outline lp-btn-big" href={PLAY_ORIGIN}>
        {T.second}
      </a>
    );

  return (
    <div className="lp lp-audience" ref={page} dir={RTL_LANGUAGES.has(lang) ? "rtl" : "ltr"} lang={lang}>
      <header className="lp-nav">
        <div className="lp-wrap lp-nav-in">
          <a className="lp-logo" href="/" aria-label="Make It">
            <img src={libraryUrl("ui/logo.png")} alt="Make It" />
          </a>
          <div className="lp-actions">
            <LanguagePicker label={L.nav.language} />
            <a className="lp-link lp-hide-s" href={audiencePath(other)}>
              {other === "schools" ? A.schools : A.special}
            </a>
            <a className="lp-btn lp-btn-ghost lp-hide-s" href="/">
              {L.nav.login}
            </a>
            <a className="lp-btn lp-btn-primary" href={start}>
              {L.nav.signup}
            </a>
          </div>
        </div>
      </header>

      <main>
        <section className="lp-hero">
          <Confetti />
          <div className="lp-wrap lp-hero-in">
            <div className="lp-hero-text">
              <span className="lp-eyebrow">{T.eyebrow}</span>
              <h1>{T.heroTitle}</h1>
              <p className="lp-lead">{T.heroSub}</p>
              <div className="lp-cta">
                <a className="lp-btn lp-btn-primary lp-btn-big" href={start}>
                  {T.start}
                </a>
                {second}
              </div>
              <p className="lp-note">{L.plans.note}</p>
            </div>
            <div className="lp-hero-art" aria-hidden>
              {ART[audience].hero}
            </div>
          </div>
        </section>

        <section className="lp-section">
          <div className="lp-wrap">
            <Heading title={T.tilesTitle} />
            <div className="lp-grid">
              {T.tiles.map((it, i) => (
                <div key={i} className={`lp-tile lp-reveal tint-${i % 4}`}>
                  <span className="lp-tile-icon">
                    <Icon name={TILE_ICONS[audience][i]} />
                  </span>
                  <h3>{it.title}</h3>
                  <p>{it.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {T.sections.map((s, i) => (
          <Feature key={i} tint={TINTS[i % TINTS.length]} flip={i % 2 === 1} kicker={s.kicker} title={s.title} text={s.text} bullets={s.bullets} art={ART[audience].sections[i]} />
        ))}

        <section className="lp-final">
          <Confetti light />
          <div className="lp-wrap lp-final-in">
            <h2>{T.finalTitle}</h2>
            <p>{T.finalSub}</p>
            <div className="lp-cta">
              <a className="lp-btn lp-btn-white lp-btn-big" href={start}>
                {T.start}
              </a>
              <a className="lp-btn lp-btn-clear lp-btn-big" href={audiencePath(other)}>
                {A.pages[other].title}
              </a>
            </div>
          </div>
        </section>
      </main>

      <footer className="lp-foot">
        <div className="lp-wrap lp-foot-bottom">
          <span>© {new Date().getFullYear()} Learny Land · Planet Factory</span>
          <span>
            <a href={LINKS.terms}>{L.footer.terms}</a>
            <a href={LINKS.privacy}>{L.footer.privacy}</a>
            <a href={LINKS.cookies}>{L.footer.cookies}</a>
            <a href={`mailto:${LINKS.email}`}>{LINKS.email}</a>
          </span>
        </div>
      </footer>
    </div>
  );
}
